import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Database, Calculator, Image, Bookmark, Stars, Menu, ChevronLeft, ChevronRight } from 'lucide-react';

const Sidebar = ({ activeSection, setActiveSection, expanded, setExpanded }) => {
  const sidebarItems = [
    {
      id: 'explore-database',
      name: 'Explore Database',
      icon: Database,
      active: activeSection === 'explore-database',
    },
    {
      id: 'calculate-habitability',
      name: 'Calculate Habitability',
      icon: Calculator,
      active: activeSection === 'calculate-habitability',
    },
    {
      id: 'image-generation',
      name: 'Image Generation',
      icon: Image,
      active: activeSection === 'image-generation',
    },
    {
      id: 'saved-exo-planets',
      name: 'Saved Exo-Planets',
      icon: Bookmark,
      active: activeSection === 'saved-exo-planets',
    },
  ];

  return (
    <motion.aside
      animate={{ width: expanded ? 256 : 80 }}
      className={`fixed left-0 top-0 h-screen bg-black/90 border-r border-gray-900 shadow-xl flex flex-col justify-between z-30 backdrop-blur-md transition-all duration-300`}
      style={{ width: expanded ? 256 : 80 }}
    >
      {/* Top Section: Logo & Toggle */}
      <div className="flex flex-col items-center pt-6 pb-2 relative">
        <button
          className="absolute -right-4 top-2 bg-blue-900/80 rounded-full p-1 shadow-lg border border-blue-700 z-20"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ChevronLeft className="w-5 h-5 text-white" /> : <ChevronRight className="w-5 h-5 text-white" />}
        </button>
        <button
          className="flex items-center space-x-3 focus:outline-none group mt-2"
          onClick={() => setActiveSection && setActiveSection('habitability-analysis')}
          style={{ background: 'none', border: 'none', padding: 0 }}
        >
          <Stars className="w-7 h-7 text-white group-hover:text-blue-400 transition-colors duration-300" />
          <AnimatePresence>
            {expanded && (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="text-2xl font-light text-white tracking-wide group-hover:text-blue-400 transition-colors duration-300 ml-2"
                style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
              >
                Stellar
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>
      {/* Main Navigation */}
      <nav className="flex-1 flex flex-col gap-2 px-2 mt-8">
        {sidebarItems.map((item) => {
          const Icon = item.icon;
          return (
            <motion.button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full flex items-center ${expanded ? 'gap-4 px-5 py-3' : 'justify-center py-3'} rounded-lg transition-all duration-300 font-light text-base tracking-wide
                ${item.active
                  ? 'bg-gradient-to-r from-blue-900/70 to-blue-700/40 text-white shadow-lg border border-blue-500/60'
                  : 'text-gray-300 hover:text-white hover:bg-gray-900/40 border border-transparent'}
              `}
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              <Icon className="w-6 h-6" />
              <AnimatePresence>
                {expanded && (
                  <motion.span
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    className="whitespace-nowrap"
                  >
                    {item.name}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          );
        })}
      </nav>
      {/* Utility Links at Bottom */}
      <div className={`flex flex-col gap-2 px-2 pb-8 ${expanded ? '' : 'items-center'}`}>
        <motion.button
          onClick={() => setActiveSection('how-to-use')}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          className={`w-full flex items-center ${expanded ? 'gap-4 px-5 py-3' : 'justify-center py-3'} rounded-lg transition-all duration-300 font-light text-base tracking-wide
            ${activeSection === 'how-to-use'
              ? 'bg-gradient-to-r from-blue-900/70 to-blue-700/40 text-white shadow-lg border border-blue-500/60'
              : 'text-gray-300 hover:text-white hover:bg-gray-900/40 border border-transparent'}
          `}
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          <span className="w-6 h-6 flex items-center justify-center"><Menu /></span>
          <AnimatePresence>
            {expanded && (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="whitespace-nowrap"
              >
                How to Use
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
        <motion.button
          onClick={() => setActiveSection('about-us')}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          className={`w-full flex items-center ${expanded ? 'gap-4 px-5 py-3' : 'justify-center py-3'} rounded-lg transition-all duration-300 font-light text-base tracking-wide
            ${activeSection === 'about-us'
              ? 'bg-gradient-to-r from-blue-900/70 to-blue-700/40 text-white shadow-lg border border-blue-500/60'
              : 'text-gray-300 hover:text-white hover:bg-gray-900/40 border border-transparent'}
          `}
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          <span className="w-6 h-6 flex items-center justify-center"><Bookmark /></span>
          <AnimatePresence>
            {expanded && (
              <motion.span
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="whitespace-nowrap"
              >
                About Us
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </motion.aside>
  );
};

export default Sidebar; 